/**
 * BlockRenderer
 *
 * Shared utilities for rendering page_content blocks inside any experience
 * container (modal, popup, banner, full page). Extracts the normalizeBlocks +
 * renderBlock logic so all experience types can reuse the block system.
 */

import { useEffect, useState } from 'react';
import type { FunnelOffer, StepPageBlock, StepPageContent } from '../types';
import { extractBlocksFromContent, normalizePageBlocks } from '@template-project/funnel-block-render';
import type { BlockCallbacks } from './ComponentRenderers';
import type { BlockContentImplProps } from './block-content-impl';
import { FidelityBlockWrapper } from './block-fidelity-wrapper';
import { funnelLogger } from '../logger';

// Re-export the fallback renderer so existing consumers (and the package
// barrel) keep importing it from BlockRenderer.
export { renderFallbackBlock } from './fallback-block-renderer';

// ---------------------------------------------------------------------------
// Lazy block-content loader
// ---------------------------------------------------------------------------
//
// The heavy ComponentRenderers switch (~3.2k LOC + ~200 styled-components) is
// pulled in via a dynamic import so it ships in a separate chunk that only
// loads when a funnel step actually renders blocks — most storefront page views
// mount FunnelExperience without an active block layout and never pay for it.
//
// A manual loader (rather than React.lazy) is used so that, once the chunk is
// resolved, blocks render synchronously with no Suspense boundary — this avoids
// a flash on funnel pages when the chunk is preloaded, and keeps synchronous
// rendering in tests that call `preloadBlockContent()` up front.

type BlockContentComponent = (props: BlockContentImplProps) => React.ReactElement | null;

let cachedBlockContent: BlockContentComponent | null = null;
let blockContentPromise: Promise<BlockContentComponent> | null = null;

/** Kick off (or reuse) the dynamic import of the block-content chunk. */
export function preloadBlockContent(): Promise<BlockContentComponent> {
  if (cachedBlockContent) return Promise.resolve(cachedBlockContent);
  if (!blockContentPromise) {
    blockContentPromise = import('./block-content-impl').then((mod) => {
      cachedBlockContent = mod.default as BlockContentComponent;
      return cachedBlockContent;
    });
  }
  return blockContentPromise;
}

function LazyBlockContent(props: BlockContentImplProps): React.ReactElement | null {
  // Lazy initializer (() => value) so React stores the component function as
  // state instead of invoking it as an initializer.
  const [Comp, setComp] = useState<BlockContentComponent | null>(() => cachedBlockContent);

  useEffect(() => {
    if (Comp) return;
    let active = true;
    void preloadBlockContent().then((loaded) => {
      if (active) setComp(() => loaded);
    });
    return () => {
      active = false;
    };
  }, [Comp]);

  if (!Comp) return null;
  return <Comp {...props} />;
}

// ---------------------------------------------------------------------------
// Block normalization
// ---------------------------------------------------------------------------

export function normalizeBlocks(pageContent?: StepPageContent | StepPageBlock[] | null): StepPageBlock[] {
  if (!pageContent) return [];
  if (Array.isArray(pageContent)) {
    return normalizePageBlocks(pageContent as Parameters<typeof normalizePageBlocks>[0]) ?? [];
  }
  const extracted = extractBlocksFromContent(pageContent);
  return normalizePageBlocks(extracted) ?? [];
}

export function hasPageContentBlocks(pageContent?: StepPageContent | StepPageBlock[] | null): boolean {
  return normalizeBlocks(pageContent).length > 0;
}

// ---------------------------------------------------------------------------
// Render all blocks (ComponentRenderers first, fallback second)
// ---------------------------------------------------------------------------

function renderBlockTree(
  block: StepPageBlock,
  offer: FunnelOffer,
  callbacks: BlockCallbacks,
  offers?: FunnelOffer[],
): React.ReactElement | null {
  // FidelityBlockWrapper injects the responsive-merged block onto its direct
  // child via cloneElement, so LazyBlockContent must be that direct child.
  const wrapped = (
    <FidelityBlockWrapper block={block} key={block.id}>
      <LazyBlockContent block={block} offer={offer} callbacks={callbacks} offers={offers} />
    </FidelityBlockWrapper>
  );

  if (Array.isArray(block.children) && block.children.length > 0) {
    const childEls = block.children
      .map((child) => renderBlockTree(child, offer, callbacks, offers))
      .filter(Boolean) as React.ReactElement[];
    if (childEls.length) {
      return (
        <div key={`${block.id}-tree`}>
          {wrapped}
          <div data-block-children={block.id}>{childEls}</div>
        </div>
      );
    }
  }
  return wrapped;
}

export function renderBlocks(
  blocks: StepPageBlock[],
  offer: FunnelOffer,
  callbacks: BlockCallbacks,
  offers?: FunnelOffer[],
): React.ReactElement[] {
  // Kick off the block-content chunk as soon as we know blocks will render, so
  // it is (often) resolved by the time LazyBlockContent's effect runs.
  if (blocks.length > 0) void preloadBlockContent();

  return blocks
    .map((block) => {
      try {
        return renderBlockTree(block, offer, callbacks, offers);
      } catch (err) {
        funnelLogger.error('[BlockRenderer] Failed to render block', err instanceof Error ? err : undefined, {
          blockId: block.id,
          blockType: block.type,
        });
        return <div key={block.id} style={{ display: 'none' }} data-block-error={block.type} />;
      }
    })
    .filter(Boolean) as React.ReactElement[];
}

export function blocksHaveCta(blocks: StepPageBlock[]): boolean {
  const ctaTypes = new Set(['cta-button-group', 'cta_button_group', 'cta_button', 'cta', 'leads-cta', 'button']);
  return blocks.some((b) => ctaTypes.has(b.type));
}
